import { PaymentModuleId } from '@foundation/apis/payments';
import { SupportedLanguage } from '@foundation/localization';
import Clarity from '@microsoft/clarity';
import type { OnApproveData } from '@paypal/paypal-js';
import {
  PayPalButtons,
  PayPalScriptProvider,
  usePayPalScriptReducer,
} from '@paypal/react-paypal-js';
import { useAuth } from '@shared/auth';
import { useTranslation } from '@shared/localization';
import { useEffect, useMemo, useState, JSX } from 'react';
import { useSearchParams } from 'react-router-dom';

import { LoadingSpinner, LoadingSpinnerSize } from '@/components/common';
import { AlertError } from '@/components/common/alerts';
import { RedirectStatus } from '@/components/contexts/params';
import { useUserFriendlyPaymentModuleName } from '@/hooks/useUserFriendlyPaymentModuleName';
import { triggerErrorToast, triggerInfoToast } from '@/libs/toasts';
import { PaymentIntentFlow } from '@/schemas/PaymentIntent';
import BraintreePayPalPayLaterService from '@/services/braintree/BraintreePayPalPayLaterService';
import { FUNDING_SOURCE } from '@/services/braintree/schemas/BraintreeFundingSources';
import Logger, { LoggerOrigin } from '@/services/Logger';
import NavigationService, {
  InternalPage,
} from '@/services/navigation/NavigationService';
import PaymentStore from '@/services/payments/PaymentStore';
import TelemetryService from '@/services/telemetry/TelemetryService';
import { TelemetryEvent } from '@/telemetry';
import { getQaTestingProps, QaTestingElements } from '@/utils/qaTesting';

interface IPayWithBraintreePayPalPayLaterBtnProps {
  navigation?: JSX.Element | null;
}

export function PayWithBraintreePayPalPayLaterBtn({
  navigation,
}: IPayWithBraintreePayPalPayLaterBtnProps) {
  const {
    i18n: { language },
  } = useTranslation();

  const scriptOptions = useMemo(
    () =>
      BraintreePayPalPayLaterService.getPayPalPayLaterScriptOptions(
        language as SupportedLanguage,
      ),
    [language],
  );

  return (
    <PayPalScriptProvider options={scriptOptions}>
      <PayPalButtonWrapper navigation={navigation} />
    </PayPalScriptProvider>
  );
}

function IneligibleNotice({ text }: { text: string }) {
  useEffect(() => {
    TelemetryService.trackEvent({
      name: TelemetryEvent.PayPalPayLaterIneligibleDisplayed,
      properties: {
        pid: PaymentStore.paymentIntentId,
      },
    });
  }, []);

  return (
    <div className="p-4 mb-4 text-sm text-center text-red-800 rounded-lg bg-red-50 dark:bg-gray-800 dark:text-red-400">
      {text}
    </div>
  );
}

function PayPalButtonWrapper({
  navigation,
}: IPayWithBraintreePayPalPayLaterBtnProps) {
  const { userId } = useAuth();
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const paymentModuleT = useUserFriendlyPaymentModuleName();
  const [{ isPending }] = usePayPalScriptReducer();
  const [searchParams, setSearchParams] = useSearchParams();

  const [err, setErr] = useState<Error>();

  const styles = {
    notEligibleText:
      'p-4 mb-4 text-sm text-center text-red-800 rounded-lg bg-red-50 dark:bg-gray-800 dark:text-red-400',
    button: BraintreePayPalPayLaterService.getPayPalBtnStyle(
      PaymentStore.theme,
    ),
  };

  const createOrder = useMemo(() => {
    return () =>
      BraintreePayPalPayLaterService.createOrder(language as SupportedLanguage);
  }, [language]);

  const proceedToAuthorize = async (data: OnApproveData) => {
    const currentPage = NavigationService.getInternalPage();
    NavigationService.navigateTo({ page: InternalPage.StatusProcessing });

    return BraintreePayPalPayLaterService.getTokenizedPayloadNonce(data)
      .then((nonce) =>
        PaymentStore.flow === PaymentIntentFlow.Manage
          ? BraintreePayPalPayLaterService.tokenize(nonce, userId)
          : BraintreePayPalPayLaterService.authorize(nonce),
      )
      .then(() => {
        if (PaymentStore.flow === PaymentIntentFlow.Manage) {
          searchParams.set('status', RedirectStatus.Success);
          setSearchParams(searchParams);
          window.location.reload();
        } else {
          NavigationService.navigateTo({ page: InternalPage.StatusSuccess });
        }
        Clarity.setTag('pay', [
          RedirectStatus.Success,
          paymentModuleT(PaymentModuleId.BraintreePayPalPayLater),
        ]);
        TelemetryService.trackEvent({
          name: TelemetryEvent.PayPalPayLaterAuthorized,
          properties: {
            pid: PaymentStore.paymentIntentId,
          },
        });
      })
      .catch((e: Error) => {
        NavigationService.navigateTo(currentPage);
        TelemetryService.trackException(e, 'BraintreePayPalPayLaterBtn');
        Logger.error(
          LoggerOrigin.ReactComponent,
          'PayPal authorization failed.',
          e,
        );
        Clarity.setTag('pay', [
          RedirectStatus.Failure,
          paymentModuleT(PaymentModuleId.BraintreePayPalPayLater),
        ]);
        triggerErrorToast(
          t('Something went wrong!'),
          t(
            'Operation failed. Please try again with a different payment method.',
          ),
        );
        setErr(e);
      });
  };

  if (err) {
    return (
      <AlertError
        title={t('Something went wrong!')}
        description={t(
          'Operation failed. Please try again with a different payment method.',
        )}
      />
    );
  }

  if (isPending) {
    return (
      <div className="h-14">
        <LoadingSpinner size={LoadingSpinnerSize.Small} />
      </div>
    );
  }

  return (
    <div
      className="h-14 flex flex-row space-x-2"
      {...getQaTestingProps(QaTestingElements.PaypalPayLaterButton)}
    >
      {navigation}
      <div className="w-full">
        <PayPalButtons
          style={styles.button}
          forceReRender={[PaymentStore.theme]}
          onApprove={proceedToAuthorize}
          createOrder={createOrder}
          fundingSource={FUNDING_SOURCE.PAYLATER}
          onInit={() => {
            Logger.info(LoggerOrigin.PayPalButton, 'onInit');
            TelemetryService.trackEvent({
              name: TelemetryEvent.PayPalPayLaterButtonRendered,
              properties: {
                pid: PaymentStore.paymentIntentId,
              },
            });
          }}
          onError={(err) => {
            triggerErrorToast(
              t('Something went wrong!'),
              t('Select a new payment method and proceed to pay.'),
            );
            Logger.error(LoggerOrigin.PayPalButton, 'onError', err);
            TelemetryService.trackException(undefined, 'PayPalButtons', {
              type: 'PayPalButtons.onError',
              err: err,
            });
          }}
          onCancel={() => {
            triggerInfoToast(
              t('Select a new payment method and proceed to pay.'),
            );
            Clarity.setTag('pay', [
              RedirectStatus.Canceled,
              paymentModuleT(PaymentModuleId.BraintreePayPalPayLater),
            ]);
          }}
        >
          <IneligibleNotice
            text={t('Sorry, you are not eligible for PayPal Pay Later')}
          />
        </PayPalButtons>
      </div>
    </div>
  );
}
